import { useCallback, useMemo } from 'react';
import { z } from 'zod';

import { useNavigationContext } from 'menu-items/navigationContext';
import { type UiContextResult, useCompleteUiContextRequestMutation } from 'store/api/aiChatApi';

type UiContextReader = () => unknown | Promise<unknown>;
type CompleteUiContextRequest = ReturnType<typeof useCompleteUiContextRequestMutation>[0];

const UI_CONTEXT_REQUEST_ID_PATTERN = /^ui_ctx_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export interface AiChatDataPart {
    type: string;
    data: unknown;
}

const uiContextRequestSchema = z
    .object({
        requestId: z.string().regex(UI_CONTEXT_REQUEST_ID_PATTERN),
        context: z.string(),
    })
    .strict();

async function postUiContextResult(
    completeUiContextRequest: CompleteUiContextRequest,
    requestId: string,
    body: UiContextResult
): Promise<void> {
    await completeUiContextRequest({ requestId, body }).unwrap();
}

async function dispatchUiContextRequest(
    completeUiContextRequest: CompleteUiContextRequest,
    requestId: string,
    context: string,
    readers: ReadonlyMap<string, UiContextReader>
): Promise<void> {
    const reader = readers.get(context);

    if (!reader) {
        await postUiContextResult(completeUiContextRequest, requestId, { error: 'UNSUPPORTED_CONTEXT' });
        return;
    }

    let result: unknown;

    try {
        result = await reader();
    } catch {
        await postUiContextResult(completeUiContextRequest, requestId, { error: 'CONTEXT_READ_FAILED' });
        return;
    }

    await postUiContextResult(completeUiContextRequest, requestId, { result });
}

export function useUiContextRequestHandler() {
    const navigationContext = useNavigationContext();
    const [completeUiContextRequest] = useCompleteUiContextRequestMutation();
    const readers = useMemo<ReadonlyMap<string, UiContextReader>>(
        () => new Map([['navigation', () => navigationContext]]),
        [navigationContext]
    );

    const handleDataPart = useCallback(
        (dataPart: AiChatDataPart): boolean => {
            if (dataPart.type !== 'data-ui-context-request') {
                return false;
            }

            const parsed = uiContextRequestSchema.safeParse(dataPart.data);
            if (!parsed.success) {
                return true;
            }

            const { requestId, context } = parsed.data;
            void dispatchUiContextRequest(completeUiContextRequest, requestId, context, readers).catch(() => undefined);
            return true;
        },
        [completeUiContextRequest, readers]
    );

    return { handleDataPart };
}
